'use client';

/**
 * LazyLiquidEther
 * ===============
 *
 * Performance wrapper around LiquidEther.
 *
 * WHY THIS EXISTS:
 * LiquidEther pulls in three.js (~525 KB raw / ~130 KB gzipped). When it was
 * imported statically, every page shipped three.js in its initial bundle and
 * initialised a WebGL fluid simulation during page load. That was the main
 * source of Total Blocking Time (330-470 ms on mobile Lighthouse).
 *
 * HOW IT WORKS:
 * 1. First paint renders StaticGradientBackground (pure CSS, same palette),
 *    so there is never an empty background.
 * 2. `next/dynamic` puts LiquidEther + three.js in a separate chunk that is
 *    NOT downloaded with the page.
 * 3. The chunk is only requested after the first real user interaction
 *    (pointer move, touch, scroll, key press). Parsing three.js and building
 *    the WebGL scene therefore never competes with LCP / hydration.
 * 4. Users who prefer reduced motion never download it at all.
 */

import dynamic from 'next/dynamic';
import { useEffect, useState, type ComponentProps } from 'react';
import StaticGradientBackground from './StaticGradientBackground';

const BRAND_COLORS = ['#D4A574', '#8B6914', '#C9A227'];

const LiquidEther = dynamic(() => import('./LiquidEther'), {
  ssr: false,
  // Keep the gradient on screen while the three.js chunk downloads
  loading: () => <StaticGradientBackground colors={BRAND_COLORS} />,
});

type LiquidEtherProps = ComponentProps<typeof LiquidEther>;

const INTERACTION_EVENTS = ['pointermove', 'pointerdown', 'touchstart', 'scroll', 'keydown', 'wheel'] as const;

export default function LazyLiquidEther(props: LiquidEtherProps) {
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const activate = () => {
      setShouldLoad(true);
      INTERACTION_EVENTS.forEach((evt) => window.removeEventListener(evt, activate));
    };

    INTERACTION_EVENTS.forEach((evt) =>
      window.addEventListener(evt, activate, { once: true, passive: true })
    );

    return () => {
      INTERACTION_EVENTS.forEach((evt) => window.removeEventListener(evt, activate));
    };
  }, []);

  if (!shouldLoad) return <StaticGradientBackground colors={props.colors ?? BRAND_COLORS} />;

  return <LiquidEther {...props} />;
}
